import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, HeartStraight, Buildings, GoogleLogo } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

const NOVA_LOGO = "https://customer-assets.emergentagent.com/job_nova-fit-meals/artifacts/8r5uywp4_Nova%20Foods.png";

const LoginPage = () => {
  const navigate = useNavigate();
  const [choice, setChoice] = useState(null); // null | 'existing' | 'new'

  const handleGoogleLogin = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin + '/auth/google/callback');
    const scope = encodeURIComponent('openid email profile');
    window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&access_type=offline&prompt=consent`;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex flex-col relative overflow-hidden">
      {/* Decorative background */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#34D399]/15 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#FBBF24]/15 rounded-full blur-3xl" />
      </div>

      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-2xl border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 font-heading text-2xl font-semibold text-[#1A4331]" data-testid="login-logo">
            <img src={NOVA_LOGO} alt="Nova Foods" className="h-10 w-10 rounded-lg object-cover" />
            Nova Foods
          </button>
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-stone-600 hover:text-[#1A4331] font-medium">
            <ArrowLeft size={20} weight="bold" /> Home
          </button>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12 relative z-10">
        <div className="w-full max-w-2xl">
          <AnimatePresence mode="wait">
            {/* GATE: pick existing vs new */}
            {choice === null && (
              <motion.div
                key="gate"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="text-center"
                data-testid="login-gate"
              >
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A4331] mb-4">Welcome to Nova Foods</p>
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-[#1C1917] font-heading mb-4">
                  Are you an <span className="italic">existing</span> customer?
                </h1>
                <p className="text-base text-stone-600 mb-10 max-w-md mx-auto">
                  Login is only for customers who already have an active subscription or catering order.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Existing customer */}
                  <motion.button
                    whileHover={{ y: -4 }}
                    onClick={() => setChoice('existing')}
                    className="group bg-white border border-stone-100 hover:border-[#1A4331] rounded-3xl p-7 text-left shadow-sm hover:shadow-xl transition-all"
                    data-testid="existing-customer-btn"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-[#1A4331]/10 flex items-center justify-center mb-4 group-hover:bg-[#1A4331] transition-colors">
                      <GoogleLogo size={22} weight="bold" className="text-[#1A4331] group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1C1917] mb-1.5">Yes, I have an account</h3>
                    <p className="text-sm text-stone-600 leading-relaxed">Sign in with Google to track your subscription and orders.</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[#1A4331] text-sm font-semibold">
                      Sign in <ArrowRight size={14} weight="bold" />
                    </div>
                  </motion.button>

                  {/* New customer */}
                  <motion.button
                    whileHover={{ y: -4 }}
                    onClick={() => setChoice('new')}
                    className="group bg-white border border-stone-100 hover:border-[#FBBF24] rounded-3xl p-7 text-left shadow-sm hover:shadow-xl transition-all"
                    data-testid="new-customer-btn"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-[#FBBF24]/20 flex items-center justify-center mb-4 group-hover:bg-[#F59E0B] transition-colors">
                      <HeartStraight size={22} weight="duotone" className="text-[#78350F]" />
                    </div>
                    <h3 className="text-lg font-semibold text-[#1C1917] mb-1.5">No, I&apos;m new here</h3>
                    <p className="text-sm text-stone-600 leading-relaxed">Browse our fitness plans or order corporate catering — no account needed to start.</p>
                    <div className="mt-4 inline-flex items-center gap-1.5 text-[#78350F] text-sm font-semibold">
                      Browse options <ArrowRight size={14} weight="bold" />
                    </div>
                  </motion.button>
                </div>

                <p className="text-xs text-stone-400 mt-8">You&apos;ll be able to log in and track orders once your first subscription or catering order is placed.</p>
              </motion.div>
            )}

            {/* EXISTING PATH: Google OAuth */}
            {choice === 'existing' && (
              <motion.div
                key="existing"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              >
                <div className="bg-white border border-stone-100 shadow-xl shadow-stone-200/40 rounded-3xl p-8 sm:p-10" data-testid="login-card">
                  <button onClick={() => setChoice(null)} className="text-sm text-stone-500 hover:text-[#1A4331] font-medium mb-6 flex items-center gap-1.5">
                    <ArrowLeft size={14} weight="bold" /> Back
                  </button>
                  <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1C1917] font-heading mb-3 text-center" data-testid="login-heading">
                    Welcome back
                  </h1>
                  <p className="text-base text-stone-600 text-center mb-8">
                    Sign in with the Google account you used at signup.
                  </p>

                  <button
                    onClick={handleGoogleLogin}
                    className="w-full bg-white border-2 border-stone-200 hover:border-[#1A4331] text-stone-800 px-6 py-4 rounded-full font-medium transition-all duration-200 flex items-center justify-center gap-3 shadow-sm hover:shadow"
                    data-testid="google-login-btn"
                  >
                    <svg width="20" height="20" viewBox="0 0 48 48">
                      <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                      <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                      <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                      <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                    </svg>
                    Continue with Google
                  </button>

                  <p className="text-xs text-stone-400 mt-6 text-center">
                    We&apos;ll only let you in if you have an active subscription or a past catering order.
                  </p>
                </div>
              </motion.div>
            )}

            {/* NEW PATH: Browse plans / catering */}
            {choice === 'new' && (
              <motion.div
                key="new"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                data-testid="new-user-options"
              >
                <div className="bg-white border border-stone-100 shadow-xl shadow-stone-200/40 rounded-3xl p-8 sm:p-10">
                  <button onClick={() => setChoice(null)} className="text-sm text-stone-500 hover:text-[#1A4331] font-medium mb-6 flex items-center gap-1.5">
                    <ArrowLeft size={14} weight="bold" /> Back
                  </button>
                  <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#1C1917] font-heading mb-3 text-center">
                    Let&apos;s get you started
                  </h1>
                  <p className="text-base text-stone-600 text-center mb-8">
                    Pick a path — no account needed until you place your first order.
                  </p>

                  <div className="space-y-3">
                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={() => navigate('/plans')}
                      className="w-full bg-gradient-to-br from-[#1A4331] to-[#2A5A44] text-white p-5 rounded-2xl flex items-center gap-4 shadow-lg shadow-[#1A4331]/20 hover:shadow-xl transition-shadow"
                      data-testid="new-view-plans"
                    >
                      <div className="w-12 h-12 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
                        <HeartStraight size={22} weight="duotone" className="text-[#34D399]" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold">Fitness Meal Subscription</p>
                        <p className="text-sm text-white/70">Weekly &amp; monthly plans for Weight Loss / Muscle Gain</p>
                      </div>
                      <ArrowRight size={18} weight="bold" />
                    </motion.button>

                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={() => navigate('/corporate-catering')}
                      className="w-full bg-gradient-to-br from-[#FBBF24] to-[#F59E0B] text-[#78350F] p-5 rounded-2xl flex items-center gap-4 shadow-lg shadow-amber-500/25 hover:shadow-xl transition-shadow"
                      data-testid="new-view-catering"
                    >
                      <div className="w-12 h-12 rounded-xl bg-white/40 border border-white/60 flex items-center justify-center flex-shrink-0">
                        <Buildings size={22} weight="duotone" className="text-[#78350F]" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-semibold">Corporate Catering</p>
                        <p className="text-sm text-[#78350F]/80">Meals for offices, events, workshops (20-500 plates)</p>
                      </div>
                      <ArrowRight size={18} weight="bold" />
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default LoginPage;
